import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { MeasureValueStatus } from '../../../types/measure.interface';

export const STATUS_LABEL_KEYS: Record<MeasureValueStatus, string> = {
  [MeasureValueStatus.Empty]: 'common.measureValueStatus.empty',
  [MeasureValueStatus.Error]: 'common.measureValueStatus.error',
  [MeasureValueStatus.OutOfBounds]: 'common.measureValueStatus.outOfBounds',
  [MeasureValueStatus.Unchecked]: 'common.measureValueStatus.unchecked',
  [MeasureValueStatus.Valid]: 'common.measureValueStatus.valid',
  [MeasureValueStatus.Pending]: 'common.measureValueStatus.pending',
};

const STATUS_BADGE_CLASSES: Record<MeasureValueStatus, string> = {
  [MeasureValueStatus.Empty]: 'bg-[#E8E8ED] text-[#62626A]',
  [MeasureValueStatus.Error]: 'bg-[#FBE9E9] text-[#DD3030]',
  [MeasureValueStatus.OutOfBounds]: 'bg-[#FFEAE0] text-[#FF5400]',
  [MeasureValueStatus.Unchecked]: 'bg-[#D4EDDC] text-[#49B66F]',
  [MeasureValueStatus.Valid]: 'bg-[#49B66F] text-white',
  [MeasureValueStatus.Pending]: 'bg-[#E3E8FF] text-[#5060CD]',
};

@Component({
  selector: 'app-measure-status-badge',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoModule],
  templateUrl: './measure-status-badge.component.html',
})
export class MeasureStatusBadgeComponent implements ICellRendererAngularComp {
  private readonly translate = inject(TranslocoService);
  private readonly translations = toSignal(this.translate.selectTranslation());

  readonly status = input<MeasureValueStatus | null | undefined>(undefined);
  protected readonly cellStatus = signal<MeasureValueStatus | null | undefined>(undefined);

  private readonly resolvedStatus = computed(() => this.status() ?? this.cellStatus());

  readonly badgeClasses = computed(() => {
    const value = this.resolvedStatus();
    if (value === null || value === undefined) return '';
    return STATUS_BADGE_CLASSES[value] ?? STATUS_BADGE_CLASSES[MeasureValueStatus.Empty];
  });

  readonly label = computed(() => {
    this.translations();
    const value = this.resolvedStatus();
    if (value === null || value === undefined) return '';
    const key = STATUS_LABEL_KEYS[value];
    return key ? this.translate.translate(key) : '';
  });

  agInit(params: ICellRendererParams<unknown, MeasureValueStatus>): void {
    this.cellStatus.set(params.value);
  }

  refresh(params: ICellRendererParams<unknown, MeasureValueStatus>): boolean {
    this.cellStatus.set(params.value);
    return true;
  }
}
